import { createAuth } from 'vue-auth3';
import router from '../../../resources/js/router';
import axios from 'axios';
import driverAuthBearer from '../../../node_modules/vue-auth3/dist/drivers/auth/bearer-token';

import type { AuthUser } from '@lib/types';

const auth = createAuth({
	rolesKey: 'permissions',
	stores: ['storage'], //, "cookie"],
	staySignedInKey: 'auth_stay_signed_in',
	tokenDefaultKey: 'auth_token',
	//
	authRedirect: '/login',
	forbiddenRedirect: '/403',
	notFoundRedirect: '/404',
	//
	loginData: {
		url: 'auth/login',
		method: 'POST',
		redirect: { name: 'Home' },
		fetchUser: true,
		staySignedIn: true,
	},
	fetchData: {
		enabled: true,
		cache: false,
	},
	refreshToken: {
		enabled: false,
		enabledInBackground: true,
	},
	plugins: {
		router,
	},
	drivers: {
		auth: driverAuthBearer,
		http: {
			request: axios,
		},
	},
});

// Add a context to the auth for easier access
auth.checkContext = function (permission: string, context: 'Event' | 'Team'): boolean {
	if (!this.check()) return false;

	// Check if user exists
	const user = this.user<AuthUser>();
	if (!user) return false;

	// Direct context permission check
	const context_ind = user.active_context?.findIndex((c) => c.model.replace(/^.*[\\/\\\\]/, '') == context);
	if (context_ind !== undefined) {
		const permissions = user.active_context?.[context_ind]?.context_permissions || [];
		if (permissions.includes(permission)) {
			return true;
		}
	}

	// Check for admin role. Todo: Is this needed or should we do this in the backend?
	if (user.roles?.some((r) => typeof r == 'string' && r == 'admin')) {
		return true;
	}

	// All checks failed reject access
	return false;
};

// Todo: understand why this is needed
axios.interceptors.request.use((config) => {
	if (auth.check()) {
		config.headers['Authorization'] = 'Bearer ' + auth.token();
	}
	return config;
});

// vue-auth3 keeps a stored token even when the server rejects it (it only self-clears a token that
// is already gone). So when the initial user fetch is answered with 401 — a stale token, e.g. after
// the account was removed — clear it locally and fall back to a clean login instead of looping on 401s.
axios.interceptors.response.use(
	(response) => response,
	(error) => {
		const url: string = error?.config?.url ?? '';
		if (error?.response?.status === 401 && /(^|\/)auth\/user(\?|$)/.test(url)) {
			auth.logout({ makeRequest: false, redirect: '/login' });
		}
		return Promise.reject(error);
	},
);

// Route permission guard — runs after auth is registered so auth.check() is available
router.beforeEach((to) => {
	const permissions: string[] | undefined = to.meta.permissions as string[] | undefined;
	if (!permissions || permissions.length === 0) return true;

	const hasAll = permissions.every((p) => auth.check(p));
	if (!hasAll) return { name: 'Forbidden' };

	return true;
});

export default auth;
